import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation } from "react-router-dom";
import { apiCall } from "../../utils/apiService";
import MessageHeaderLayer from "./MessageHeaderLayer";
import CodeSamplesPanel from "./CodeSamplesPanel";
import ToggleBar from "../child/ToggleBar";
import "./ApiTestPage.css";

const mediaMetaFromPath = (pathname = "") => {
  if (pathname.includes("audio")) {
    return { title: "Audio message test", badge: "POST · Audio", accept: "audio/*" };
  }
  if (pathname.includes("video")) {
    return { title: "Video message test", badge: "POST · Video", accept: "video/*" };
  }
  return { title: "Image message test", badge: "POST · Image", accept: "image/*" };
};

const MediaLayer = () => {
  const location = useLocation();
  const meta = mediaMetaFromPath(location.pathname);
  const [templates, setTemplates] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [data, setData] = useState({
    file: null,
    client_id: null,
    token: null,
    mobile: "",
    fromTemplet: false,
    message: "",
    saveAsTemplet: false,
    name: null,
    mimetype: null,
    filename: null,
    onlySave: false,
    type: "media",
  });

  const fetchTemplates = async (type) => {
    try {
      const response = await apiCall({
        url: `templet/get-templet`,
        method: "POST",
        body: { type },
      });
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const hideAlert = (sec = 1) => {
    setTimeout(() => {
      setAlertMessage("");
      setSpinner(false);
    }, sec * 1000);
  };

  const cleanForm = () => {
    setSpinner(false);
    hideAlert(2);
  };

  const addNewTemplate = (newTemplate) => {
    setTemplates([newTemplate, ...templates]);
  };

  const validated = () => {
    if (!data.client_id) return { isValid: false, messageText: "Instance is required!" };
    if (!data.token) return { isValid: false, messageText: "Token is required!" };
    if (!data.mobile) return { isValid: false, messageText: "Phone is required!" };
    if (!data.fromTemplet && !data.file) {
      return { isValid: false, messageText: "File is required!" };
    }
    if (!data.message) return { isValid: false, messageText: "Caption / message is required!" };
    if (data.saveAsTemplet && !data.name) {
      return { isValid: false, messageText: "Template name is required!" };
    }
    return { isValid: true, messageText: "" };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { isValid, messageText } = validated();
    if (!isValid) {
      setAlertMessage({ message: messageText, class: "danger" });
      hideAlert(2);
      return;
    }

    setSpinner(true);
    try {
      const formData = new FormData();
      for (let key in data) {
        if (data[key] != null) formData.append(key, data[key]);
      }
      const response = await apiCall({
        url: "send-message/send-media",
        method: "POST",
        body: formData,
        withoutStringify: true,
      });

      setAlertMessage({
        message: response.msg,
        class: response.success ? "success" : "danger",
      });
      if (response.success) {
        if (response.template) addNewTemplate(response.template);
        cleanForm();
      } else {
        hideAlert(2);
      }
    } catch (error) {
      setAlertMessage({ message: error.message, class: "danger" });
      hideAlert(2);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setData((prev) => ({
      ...prev,
      file,
      mimetype: file.type,
      filename: file.name,
    }));
  };

  const filterTemplateById = (id) => templates.find((t) => t.id == id) || false;

  const handleSelectTemplate = (event) => {
    const selected = filterTemplateById(event.target.value);
    if (selected) {
      try {
        const file = JSON.parse(selected.content);
        const filePath = file.image?.url || "";
        const filename = filePath.substring(filePath.lastIndexOf("/") + 1);
        setData((prev) => ({
          ...prev,
          message: file.caption || "",
          file: file.image?.url,
          mimetype: selected.mimetype,
          filename,
        }));
      } catch (err) {
        console.error(err);
      }
    }
    setSelectedTemplate(event.target.value);
  };

  useEffect(() => {
    document.title = meta.title;
    fetchTemplates("media");
  }, [meta.title]);

  const sampleParams = useMemo(
    () => ({
      clientId: data.client_id,
      token: data.token,
      mobile: data.mobile,
      message: data.message,
      caption: data.message,
      filename: data.filename || "file.jpg",
    }),
    [data.client_id, data.token, data.mobile, data.message, data.filename]
  );

  return (
    <div className="api-test-page">
      <div className="card">
        <div className="card-body">
          <div className="api-test-welcome">
            <div>
              <h5>{meta.title}</h5>
              <p>Upload media, add a caption, and grab integration samples for send-media.</p>
            </div>
            <span className="api-test-badge">
              <Icon icon="mdi:api" width={14} />
              {meta.badge}
            </span>
          </div>

          <MessageHeaderLayer
            getHeaderData={(id, apiKey) =>
              setData((prev) => ({ ...prev, client_id: id || null, token: apiKey }))
            }
          />

          <div className="api-test-grid">
            <form className="api-test-card" onSubmit={handleSubmit}>
              <div className="api-test-card-head">
                <h6>Request test</h6>
                <span>Multipart upload</span>
              </div>
              <div className="api-test-card-body">
                <div className="api-test-note">
                  <Icon icon="mdi:information-outline" width={18} />
                  <span>Use it wisely — not recommended for bulk numbers from this tester.</span>
                </div>

                {alertMessage && (
                  <div className={`api-test-alert ${alertMessage.class}`}>{alertMessage.message}</div>
                )}

                <label className="api-test-label" htmlFor="media-phone">
                  Phone
                </label>
                <input
                  id="media-phone"
                  type="text"
                  className="form-control"
                  placeholder="923037900571"
                  value={data.mobile || ""}
                  onChange={(e) => setData({ ...data, mobile: e.target.value })}
                />
                <span className="api-test-help">Mobile number with country code, without +</span>

                <ToggleBar
                  id="media-template-switch"
                  label="Using template?"
                  checked={data.fromTemplet}
                  onChange={(checked) =>
                    setData({ ...data, fromTemplet: checked, saveAsTemplet: false })
                  }
                />

                {!data.fromTemplet ? (
                  <>
                    <label className="api-test-label" htmlFor="media-file">
                      Media file
                    </label>
                    <input
                      id="media-file"
                      type="file"
                      accept={meta.accept}
                      className="form-control mb-3"
                      onChange={handleFileChange}
                    />

                    <label className="api-test-label" htmlFor="media-caption">
                      Caption
                    </label>
                    <textarea
                      id="media-caption"
                      rows={4}
                      className="form-control"
                      placeholder="Type caption here..."
                      value={data.message || ""}
                      onChange={(e) => setData({ ...data, message: e.target.value })}
                    />

                    <ToggleBar
                      id="media-save-template"
                      label="Save as template?"
                      checked={data.saveAsTemplet}
                      onChange={(checked) =>
                        setData({ ...data, saveAsTemplet: checked })
                      }
                    />

                    {data.saveAsTemplet && (
                      <>
                        <label className="api-test-label" htmlFor="media-template-name">
                          Template name
                        </label>
                        <input
                          id="media-template-name"
                          type="text"
                          className="form-control"
                          value={data.name || ""}
                          onChange={(e) => setData({ ...data, name: e.target.value })}
                          placeholder="Template name"
                        />
                        <ToggleBar
                          id="media-only-save"
                          label="Save template only"
                          description="Do not send WhatsApp message — save template only."
                          checked={data.onlySave}
                          onChange={(checked) =>
                            setData({ ...data, onlySave: checked })
                          }
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <label className="api-test-label" htmlFor="media-template-select">
                      Template
                    </label>
                    <select
                      id="media-template-select"
                      className="form-select"
                      value={selectedTemplate}
                      onChange={handleSelectTemplate}
                    >
                      <option value="">Select Template</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                <div className="api-test-actions">
                  <button type="submit" className="btn api-test-submit" disabled={spinner}>
                    {spinner ? "Sending…" : "Send Message"}
                  </button>
                </div>
              </div>
            </form>

            <CodeSamplesPanel kind="media" params={sampleParams} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaLayer;
